import { useQuery, UseQueryResult, UseQueryOptions } from 'react-query'
import { getBasename } from "../utilities/getBasename"

// types
export type hucId = string

export interface RasterMetadataRecords {
    column_map: MetadataColumnMap
    data: MetadataRecordEntries
}

export type MetadataColumnMap = {
    // keys are exhaustive
    [Property in keyof MetadataRecord]: string;
};


export type MetadataRecordEntries = {
    [h: hucId]: MetadataRecord;
};

export type MetadataRecord = {
    area_km2: number
    basin: string
    elevation_m: number
    floods_count: number
    k_s: number
    kurtosis_10_m: number
    landslides_count: number
    mean_10_m: number
    mean_30_m: number
    p_mm_per_yr: number
    pet_mm_per_yr: number
    pga_g: number
    region: string
    skewness_10_m: number
    slope_degrees: number
    states: string
    theta: number
    variance_10_m: number
    w_m_per_s: number
    wetlands_percent: number
    min_90_m: number
    max_90_m: number
    std_90_m: number
    mean_90_m: number
}

export type MetadataColumnName = keyof MetadataRecord

export type MetadataAttribute<T extends MetadataColumnName> = {
    name: MetadataColumnMap[T]
    value: MetadataRecord[T]
}

export type MetadataAttributes<T extends MetadataColumnName> = Record<T, MetadataAttribute<T>>

export interface GetSingleRasterMetadata {
    getSingleRasterMetadata: ((hucId: hucId) => MetadataRecord | undefined)
    getSingleRasterMetadataWithColumns: ((hucId: hucId) => MetadataAttributes<MetadataColumnName> | undefined)
    getColumnMap: () => MetadataColumnMap | undefined
}

export type UseRasterMetadata = UseQueryResult<RasterMetadataRecords, undefined> & GetSingleRasterMetadata

export type UseRasterQueryOptions = Omit<UseQueryOptions<RasterMetadataRecords, undefined, RasterMetadataRecords, string>, 'queryKey' | 'queryFn' | 'enabled'>

// hooks
export function useRasterMetadata(filename: string, options?: UseRasterQueryOptions): UseRasterMetadata {
    const enabled = filename.length > 0
    const basename = getBasename(filename)


    const values = useQuery(basename, async () => {
        const res = await fetch(filename)
        return await res.json() as Promise<RasterMetadataRecords>
    }, { enabled: enabled, ...options })

    function getSingleRasterMetadataWithColumns(hucId: hucId): MetadataAttributes<MetadataColumnName> | undefined {
        // guard if still fetching
        if (values.isLoading) {
            console.info(`useRasterMetadata hook still loading. Method getSingleRasterMetadataWithColumns, did not return metadata for hucId ${hucId}`);
            return undefined
        }

        // guard if undefined
        if (values.data === undefined) {
            console.error(`useRasterMetadata hook method getSingleRasterMetadata, did not return metadata for hucId ${hucId}`);
            return undefined;
        }

        const { data, column_map } = values.data

        const huc: MetadataRecord = data[hucId]
        const colNames = column_map

        let o = {} as MetadataAttributes<MetadataColumnName>;

        Object.entries(huc).forEach(pair => {
            const [k, v] = pair
            o[k as MetadataColumnName] = {
                name: colNames[k as MetadataColumnName],
                value: v
            }
        })

        return o as MetadataAttributes<MetadataColumnName>;
    }

    function getColumnMap(): MetadataColumnMap | undefined {
        // guard if still fetching
        if (values.isLoading) {
            console.info(`useRasterMetadata hook still loading. Method getColumnMap, did not return column map`);
            return undefined
        }

        if (values.data === undefined) {
            console.error(`useRasterMetadata hook method getColumnMap, did not return column map`);
            return undefined;
        }

        return values.data.column_map
    }

    function getSingleRasterMetadata(hucId: hucId): MetadataRecord | undefined {
        // guard if still fetching
        if (values.isLoading) {
            console.info(`useRasterMetadata hook still loading. Method getSingleRasterMetadata, did not return metadata for hucId ${hucId}`);
            return undefined
        }

        if (values.data === undefined) {
            console.error(`useRasterMetadata hook method getSingleRasterMetadata, did not return metadata for hucId ${hucId}`);
            return undefined;
        }
        return values.data.data[hucId]
    }

    return { getColumnMap, getSingleRasterMetadataWithColumns, getSingleRasterMetadata, ...values }
}

export default useRasterMetadata;
